import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';

import { aiApi } from '../../../api/ai.js';
import { ApiError } from '../../../api/client.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { ModelFormModal } from '../../../components/ai/ModelFormModal.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { t } from '../../../lib/i18n.js';

const KIND_LABEL = { chat: 'Chat', realtime: 'Realtime', embedding: 'Embedding', transcription: 'Trascrizione' };
const KIND_TONE = { chat: 'brand', realtime: 'info', embedding: 'neutral', transcription: 'warning' };

// Format a USD amount cleanly, removing floating point noise
// (e.g. 0.13799999998 -> $0.138). Shows up to 4 fractional digits.
function usd(n) {
  const v = Number(n) || 0;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

function priceSummary(m) {
  const p = m.pricing || {};
  if (m.kind === 'chat' || m.kind === 'embedding') {
    if (p.inputPerMillion == null && p.outputPerMillion == null) return '-';
    return `in ${usd(p.inputPerMillion)} / out ${usd(p.outputPerMillion)} per 1M token`;
  }
  if (m.kind === 'realtime') {
    if (p.inputAudioPerMillionUsd == null && p.outputAudioPerMillionUsd == null) return '-';
    return `audio in ${usd(p.inputAudioPerMillionUsd)} / out ${usd(p.outputAudioPerMillionUsd)} per 1M token`;
  }
  return '-';
}

export function ModelsPage() {
  const { hasPermission } = useAuth();
  const canConfigure = hasPermission('ai:configure');
  const queryClient = useQueryClient();

  const modelsQuery = useQuery({ queryKey: ['ai', 'models'], enabled: canConfigure, queryFn: () => aiApi.models() });
  const data = modelsQuery.data;

  const [modal, setModal] = useState({ open: false, initial: null });
  const [assign, setAssign] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);

  useEffect(() => {
    if (!data) return;
    const agents = {};
    (data.agents || []).forEach((a) => { agents[a.id] = a.model || ''; });
    setAssign({ ...data.assignments, agents });
  }, [data]);

  const catalog = (data && data.catalog) || [];

  const optsByKind = useMemo(() => {
    const make = (kind) => catalog.filter((m) => m.kind === kind && m.enabled !== false).map((m) => ({ value: m.id, label: `${m.label} (${m.id})` }));
    return { chat: make('chat'), realtime: make('realtime'), embedding: make('embedding'), transcription: make('transcription') };
  }, [catalog]);

  const catalogMutation = useMutation({
    mutationFn: (models) => aiApi.setModelsCatalog(models),
    onSuccess: () => { setRemoveTarget(null); toast.success('Catalogo aggiornato.'); queryClient.invalidateQueries({ queryKey: ['ai', 'models'] }); queryClient.invalidateQueries({ queryKey: ['ai', 'playground', 'options'] }); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const assignMutation = useMutation({
    mutationFn: (body) => aiApi.setModelAssignments(body),
    onSuccess: () => { toast.success('Assegnazioni salvate.'); queryClient.invalidateQueries({ queryKey: ['ai', 'models'] }); queryClient.invalidateQueries({ queryKey: ['ai', 'playground', 'options'] }); },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const saveModel = (model) => {
    const next = (() => {
      const idx = catalog.findIndex((m) => m.id === model.id);
      if (idx >= 0) { const copy = [...catalog]; copy[idx] = model; return copy; }
      return [...catalog, model];
    })();
    catalogMutation.mutate(next);
    setModal({ open: false, initial: null });
  };

  const confirmRemove = () => {
    if (!removeTarget) return;
    catalogMutation.mutate(catalog.filter((m) => m.id !== removeTarget.id));
  };

  const optionsWithCurrent = (kind, current) => {
    const opts = optsByKind[kind] || [];
    if (current && !opts.some((o) => o.value === current)) {
      return [{ value: current, label: `${current} (non in catalogo)` }, ...opts];
    }
    return opts;
  };

  const saveAssignments = () => {
    if (!assign) return;
    // Only send agent overrides that point to a real model; an empty value
    // means "use the chat default" and would fail server validation.
    const agents = {};
    Object.entries(assign.agents || {}).forEach(([id, model]) => { if (model) agents[id] = model; });
    assignMutation.mutate({
      defaultModel: assign.defaultModel,
      embeddingsModel: assign.embeddingsModel,
      transcribeModel: assign.transcribeModel,
      voiceModel: assign.voiceModel,
      agents
    });
  };

  if (!canConfigure) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Modelli AI" />
        <Card><p className="text-sm text-ink-muted">Non hai il permesso <code className="rounded bg-border/40 px-1 py-0.5 text-xs">ai:configure</code>.</p></Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Modelli AI"
        description="Catalogo dei modelli e assegnazione per contesto: chat, RAG, trascrizione, voce e agenti."
        actions={<Button size="sm" onClick={() => setModal({ open: true, initial: null })}><Plus className="h-4 w-4" /> Aggiungi modello</Button>}
      />

      {/* Catalog */}
      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-ink-muted">
                <th className="px-5 py-3">Modello</th>
                <th className="px-5 py-3">Tipo</th>
                <th className="px-5 py-3">Prezzo</th>
                <th className="px-5 py-3">Stato</th>
                <th className="px-5 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {catalog.map((m) => (
                <tr key={m.id} className="border-b border-border/60 last:border-0">
                  <td className="px-5 py-3">
                    <div className="font-medium text-ink">{m.label}</div>
                    <div className="text-xs text-ink-faint">{m.id}</div>
                  </td>
                  <td className="px-5 py-3"><Badge tone={KIND_TONE[m.kind] || 'neutral'}>{KIND_LABEL[m.kind] || m.kind}</Badge></td>
                  <td className="px-5 py-3 text-xs text-ink-muted">{priceSummary(m)}</td>
                  <td className="px-5 py-3">{m.enabled !== false ? <Badge tone="success">attivo</Badge> : <Badge tone="neutral">disattivo</Badge>}</td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" onClick={() => setModal({ open: true, initial: m })} aria-label="Modifica" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-subtle hover:text-ink"><Pencil className="h-4 w-4" /></button>
                      <button type="button" onClick={() => setRemoveTarget(m)} aria-label="Elimina" className="rounded-md p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {catalog.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-sm text-ink-muted">Nessun modello nel catalogo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Assignments */}
      {assign && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink">Assegnazioni per contesto</h2>
            <Button size="sm" onClick={saveAssignments} loading={assignMutation.isPending}>Salva assegnazioni</Button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Select label="Chat (default)" options={optionsWithCurrent('chat', assign.defaultModel)} value={assign.defaultModel} onChange={(e) => setAssign((a) => ({ ...a, defaultModel: e.target.value }))} />
            <Select label="Embeddings (RAG)" options={optionsWithCurrent('embedding', assign.embeddingsModel)} value={assign.embeddingsModel} onChange={(e) => setAssign((a) => ({ ...a, embeddingsModel: e.target.value }))} />
            <Select label="Trascrizione" options={optionsWithCurrent('transcription', assign.transcribeModel)} value={assign.transcribeModel} onChange={(e) => setAssign((a) => ({ ...a, transcribeModel: e.target.value }))} />
            <Select label="Voce (realtime)" options={optionsWithCurrent('realtime', assign.voiceModel)} value={assign.voiceModel} onChange={(e) => setAssign((a) => ({ ...a, voiceModel: e.target.value }))} />
          </div>

          <h3 className="mb-3 mt-6 text-xs font-semibold uppercase tracking-wider text-ink-muted">Agenti (modello chat)</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {(data.agents || []).map((a) => (
              <Select
                key={a.id}
                label={a.label}
                options={[{ value: '', label: 'Default chat' }, ...optionsWithCurrent('chat', assign.agents[a.id])]}
                value={assign.agents[a.id] || ''}
                onChange={(e) => setAssign((prev) => ({ ...prev, agents: { ...prev.agents, [a.id]: e.target.value } }))}
              />
            ))}
          </div>
          <p className="mt-3 text-xs text-ink-muted">Lascia un agente sul valore vuoto per usare il modello chat di default.</p>
        </Card>
      )}

      <ModelFormModal
        open={modal.open}
        initial={modal.initial}
        existingIds={catalog.map((m) => m.id)}
        onClose={() => setModal({ open: false, initial: null })}
        onSave={saveModel}
      />

      <ConfirmDialog
        open={Boolean(removeTarget)}
        destructive
        title="Rimuovere il modello"
        description={removeTarget ? `Il modello "${removeTarget.label}" (${removeTarget.id}) sarà rimosso dal catalogo.` : ''}
        confirmLabel="Rimuovi"
        loading={catalogMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
      />
    </div>
  );
}
